import React, { useState } from "react";
import { Box, Paper, Typography, Button, TextField, Autocomplete, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import { createAgent, updateAgent, listIndexes } from "../api/agentApi";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "./UserContext";

const roleOptions = [
  "Marketplace Operations Manager",
  "Creative Director",
  "HR Talent Partner",
  "Product Manager",
  "Developer",
  "Quality Analyst",
  "Business Analyst",
  "Data Researcher",
  "Legal Expert",
  "UX Designer",
  "Project Lead",
  "Customer Success",
  "Marketing Analyst"
];

const llmOptions = ["openai", "anthropic", "azure", "google", "mistral", "ollama"];

export default function ConversationalAgentCreator({ onAgentCreated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  // If editing, get agentCard from location.state
  const editingAgentCard = location.state?.agentCard;

  // Determine if this is an update (edit) or create
  const isEdit = !!editingAgentCard;

  // Prepopulate form if editing
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    if (editingAgentCard) {
      return {
        role: editingAgentCard.role || "",
        name: editingAgentCard.name || "",
        description: editingAgentCard.description || "",
        tools: editingAgentCard.tools || [],
        llm: editingAgentCard.llm || "openai",
        temperature: editingAgentCard.llm_settings?.temperature ?? 0,
        max_tokens: editingAgentCard.llm_settings?.max_tokens ?? 4096,
        status: editingAgentCard.status || "Creating"
      };
    }
    return {
      role: "",
      name: "",
      description: "",
      tools: [],
      llm: "openai",
      temperature: 0,
      max_tokens: 4096,
      status: "Creating"
    };
  });
  // Also prepopulate selectedTools if editing
  const [selectedTools, setSelectedTools] = useState(() =>
    Array.isArray(editingAgentCard?.tools)
      ? editingAgentCard.tools.map(t => ({
          ...t,
          paramValues: {},
          // Ensure params is always an array for editing
          params: t.parameters
            ? t.parameters.map(p => ({
                name: p.name || "",
                type: p.type || "string",
                description: p.description || ""
              }))
            : t.params || []
        }))
      : []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [customTool, setCustomTool] = useState({ name: "", description: "", params: [] });
  const [showCustom, setShowCustom] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualJsonValue, setManualJsonValue] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [success, setSuccess] = useState("");

  const [docIndexes, setDocIndexes] = useState([]);
  const [codeIndexes, setCodeIndexes] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState(null);
  const [selectedCodeIndex, setSelectedCodeIndex] = useState(null);

  React.useEffect(() => {
    async function fetchIndexes() {
      try {
        const docRes = await listIndexes("doc");
        setDocIndexes(docRes.indexes || []);
        const codeRes = await listIndexes("code");
        setCodeIndexes(codeRes.indexes || []);
      } catch (e) {
        // ignore errors for now
      }
    }
    fetchIndexes();
  }, []);

  // Prepopulate selectedDocIndex and selectedCodeIndex in Edit mode if rag array is present
  React.useEffect(() => {
    if (isEdit && editingAgentCard && Array.isArray(editingAgentCard.rag)) {
      const docIdx = editingAgentCard.rag.find(idx => idx.rag_type === "doc");
      const codeIdx = editingAgentCard.rag.find(idx => idx.rag_type === "code");
      if (docIdx && docIdx.id) setSelectedDocIndex(docIdx.id);
      if (codeIdx && codeIdx.id) setSelectedCodeIndex(codeIdx.id);
    }
  }, [isEdit, editingAgentCard]);

  // Step 0: Role selection
  if (step === 0) {
    return (
      <Box component={Paper} elevation={3} sx={{ maxWidth: 600, mx: "auto", my: 8, p: 4 }}>
        <Typography variant="h4" gutterBottom>Agent Creator</Typography>
        <Typography variant="h5" gutterBottom>Let's create your agent!</Typography>
        <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}>
          <Box sx={{ mr: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>🗨️ I am a ...</Typography>
          </Box>
          <Autocomplete
            options={roleOptions}
            value={form.role}
            onChange={(_, value) => setForm(f => ({ ...f, role: value || "" }))}
            onInputChange={(_, value) => setForm(f => ({ ...f, role: value }))}
            renderInput={params => <TextField {...params} label="Type or select a role" autoFocus />}
            sx={{ minWidth: 220 }}
            disableClearable
            freeSolo
          />
        </Box>
        {form.role && (
          <Box sx={{ mt: 2, ml: 4 }}>
            <Typography variant="body2" color="text.secondary">Great! You're creating an agent as a <b>{form.role}</b>.</Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!form.role || form.role.trim() === ""}
            onClick={() => setStep(1)}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  }

  // Step 1: Intent creation
  const intentOptions = {
    "Marketplace Operations Manager": [
      "maximize shipment-driver matches",
      "analyze delivery performance",
      "manage shipment tracking",
      "handle customer inquiries"
    ],
    "Creative Director": [
      "generate creative campaigns",
      "brainstorm new product ideas",
      "develop brand strategies",
      "create marketing content",
      "conduct market research"
    ],
    "HR Talent Partner": [
      "sourcing candidates",
      "screening resumes",
      "conducting interviews",
      "onboarding new hires",
      "employee engagement"
    ],
    "Product Manager": [
      "creating PRDs",
      "doing market analysis",
      "doing competitive analysis",
      "feature prioritization",
      "user persona research",
      "roadmap planning"
    ],
    "Developer": [
      "generating API documentation",
      "code review automation",
      "bug triage",
      "test case generation",
      "refactoring suggestions"
    ],
    "Quality Analyst": [
      "creating static accessibility testing reports",
      "automating regression test suites",
      "performance benchmarking",
      "security vulnerability scanning"
    ],
    "Business Analyst": [
      "requirements gathering",
      "process mapping",
      "stakeholder analysis",
      "KPI dashboard creation"
    ],
    "Data Researcher": [
      "data cleaning",
      "exploratory data analysis",
      "trend forecasting",
      "dataset annotation"
    ],
    "Legal Expert": [
      "contract review",
      "compliance checklist generation",
      "risk assessment",
      "policy drafting"
    ],
    "UX Designer": [
      "user journey mapping",
      "usability testing",
      "wireframe generation",
      "accessibility audit"
    ],
    "Project Lead": [
      "resource allocation",
      "timeline estimation",
      "risk tracking",
      "status reporting"
    ],
    "Customer Success": [
      "migrating customer's video assets to my company",
      "onboarding new clients",
      "support ticket triage",
      "customer feedback analysis"
    ],
    "Marketing Analyst": [
      "campaign performance analysis",
      "lead scoring",
      "market segmentation",
      "content strategy planning"
    ]
  };
  if (step === 1) {
    const options = intentOptions[form.role] || [];
    return (
      <Box component={Paper} elevation={3} sx={{ maxWidth: 600, mx: "auto", my: 8, p: 4 }}>
        <Typography variant="h4" gutterBottom>Agent Creator</Typography>
        <Typography variant="h5" gutterBottom>Agent Intent</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Role: <b>{form.role}</b></Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}>
          <Box sx={{ mr: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>🗨️ ... and I want to create an agent for ...</Typography>
          </Box>
          <Autocomplete
            options={options}
            value={form.name}
            onChange={(_, value) => setForm(f => ({ ...f, name: value || "" }))}
            onInputChange={(_, value) => setForm(f => ({ ...f, name: value }))}
            renderInput={params => <TextField {...params} label="Type or select an intent/use case" autoFocus />}
            sx={{ minWidth: 280 }}
            freeSolo
            disableClearable
          />
        </Box>
        {form.name && (
          <Box sx={{ mt: 2, ml: 4 }}>
            <Typography variant="body2" color="text.secondary">Intent: <b>{form.name}</b></Typography>
          </Box>
        )}
        {/* Agent Description Section */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>Describe your agent</Typography>
          <TextField
            label="Agent Description"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            fullWidth
            multiline
            minRows={4}
            placeholder="What does your agent do? What problem does it solve?"
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button onClick={() => setStep(0)}>Back</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!form.name || form.name.trim() === "" || !form.description}
            onClick={() => setStep(2)}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  }

  // Step 2: Tool creation
  const toolSuggestions = {
    "Creative Director": [
      // { name: "Web Search", description: "Search the web for information.", params: ["query"] },
      // { name: "Content Generator", description: "Generate marketing content.", params: ["topic", "length"] },
      // { name: "Brand Analyzer", description: "Analyze brand performance.", params: ["brand_name"] }
    ],
    "HR Talent Partner": [
      // { name: "Resume Parser", description: "Extracts key information from resumes.", params: ["resume_text"] },
      // { name: "Interview Scheduler", description: "Schedules interviews with candidates.", params: ["candidate_email", "interview_date"] },
      // { name: "Candidate Sourcing", description: "Finds potential candidates based on job requirements.", params: ["job_description"] },
    ],
    "Product Manager": [
      { name: "Web Search", description: "Search the web for information.", params: ["query"] },
      { name: "Data Scraper", description: "Extract data from web pages.", params: ["url", "selector"] },
      { name: "Competitor Info Fetcher", description: "Fetch competitor details.", params: ["company_name"] }
    ],
    "Developer": [
      // { name: "Code Review Tool", description: "Automate code review.", params: ["repo_url"] },
      // { name: "API Doc Generator", description: "Generate API documentation.", params: ["source_code"] }
    ],
    "Quality Analyst": [
      { name: "Accessibility Checker", description: "Run accessibility tests.", params: ["url"] },
      { name: "Performance Tester", description: "Benchmark web performance.", params: ["url"] }
    ],
    "Business Analyst": [
      { name: "KPI Dashboard Generator", description: "Create KPI dashboards.", params: ["data_source"] },
      { name: "Process Mapper", description: "Map business processes.", params: ["process_description"] }
    ],
    "Data Researcher": [
      { name: "Data Cleaner", description: "Clean and preprocess data.", params: ["dataset"] },
      { name: "Trend Forecaster", description: "Forecast data trends.", params: ["historical_data"] }
    ],
    "Legal Expert": [
      { name: "Contract Analyzer", description: "Analyze legal contracts.", params: ["contract_text"] },
      { name: "Compliance Checker", description: "Check compliance requirements.", params: ["document"] }
    ],
    "UX Designer": [
      { name: "Usability Tester", description: "Test usability of designs.", params: ["design_url"] },
      { name: "Wireframe Generator", description: "Generate wireframes.", params: ["requirements"] }
    ],
    "Project Lead": [
      { name: "Resource Allocator", description: "Allocate project resources.", params: ["project_plan"] },
      { name: "Timeline Estimator", description: "Estimate project timelines.", params: ["tasks"] }
    ],
    "Customer Success": [
      { name: "Asset Migrator", description: "Migrate customer assets.", params: ["source", "destination"] },
      { name: "Feedback Analyzer", description: "Analyze customer feedback.", params: ["feedback_data"] }
    ],
    "Marketing Analyst": [
      { name: "Campaign Analyzer", description: "Analyze marketing campaigns.", params: ["campaign_data"] },
      { name: "Lead Scorer", description: "Score marketing leads.", params: ["lead_list"] }
    ],
    "Customer Support Assistant": [
      { name: "FAQ Retriever", description: "Retrieve answers from FAQ database.", params: ["question"] },
      { name: "Ticket Creator", description: "Create support tickets.", params: ["customer_id", "issue"] }
    ]
  };
  if (step === 2) {
    const suggestions = toolSuggestions[form.role] || [];
    const handleToolSelect = tool => {
      if (!selectedTools.some(t => t.name === tool.name)) {
        setSelectedTools([...selectedTools, { ...tool, paramValues: {} }]);
      }
    };
    const handleToolRemove = name => {
      setSelectedTools(selectedTools.filter(t => t.name !== name));
    };
    const handleParamChange = (toolIdx, param, value) => {
      const updated = [...selectedTools];
      updated[toolIdx].paramValues[param] = value;
      setSelectedTools(updated);
    };
    const handleAddCustomTool = () => {
      if (customTool.name && customTool.description) {
        setSelectedTools([...selectedTools, { ...customTool, paramValues: {} }]);
        setCustomTool({ name: "", description: "", params: [] });
        setShowCustom(false);
      }
    };
    // Tool editing logic for custom tools
    const handleEditTool = (idx, field, value) => {
      setSelectedTools(prev => {
        // Use a new array/object for the tool being edited to avoid React focus issues
        const updated = prev.map((tool, i) =>
          i === idx ? { ...tool, [field]: value } : tool
        );
        return [...updated];
      });
    };
    const handleEditParam = (toolIdx, paramIdx, field, value) => {
      setSelectedTools(prev => {
        // Deep copy the tool and params to avoid focus loss and ensure state updates
        const updated = prev.map((tool, i) => {
          if (i !== toolIdx) return tool;
          const params = tool.params ? tool.params.map((param, j) =>
            j === paramIdx ? { ...param, [field]: value } : { ...param }
          ) : [];
          return { ...tool, params: [...params] };
        });
        return [...updated];
      });
    };
    // Remove parameter
    const handleRemoveParam = (toolIdx, paramIdx) => {
      setSelectedTools(prev => {
        const updated = prev.map((tool, i) => {
          if (i !== toolIdx) return tool;
          const params = tool.params ? tool.params.filter((_, j) => j !== paramIdx) : [];
          return { ...tool, params: [...params] };
        });
        return [...updated];
      });
    };
    // Add parameter
    const handleAddParam = toolIdx => {
      setSelectedTools(prev => {
        const updated = prev.map((tool, i) => {
          if (i !== toolIdx) return tool;
          const params = [...(tool.params || []), { name: "", type: "string", description: "" }];
          return { ...tool, params };
        });
        return [...updated];
      });
    };
    // Add missing handler for invocation type
    const handleInvocationTypeChange = (toolIdx, value) => {
      setSelectedTools(prev => {
        const updated = prev.map((tool, i) => {
          if (i !== toolIdx) return tool;
          return {
            ...tool,
            invocation: { ...(tool.invocation || {}), type: value }
          };
        });
        return [...updated];
      });
    };
    return (
      <Box component={Paper} elevation={3} sx={{ maxWidth: 600, mx: "auto", my: 8, p: 4 }}>
      <Typography variant="h4" gutterBottom>Agent Creator</Typography>
      <Typography variant="h5" gutterBottom>Tool Selection</Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">Role: <b>{form.role}</b></Typography>
        <Typography variant="body2" color="text.secondary">Intent: <b>{form.name}</b></Typography>
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>🗨️ Would you like your agent to use any tools?</Typography>
      {suggestions.length > 0 && (
        <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>Here are some suggestions:</Typography>
        {suggestions.map(tool => (
          <Box key={tool.name} sx={{ mb: 1, pl: 2 }}>
          <Button variant="outlined" size="small" onClick={() => handleToolSelect(tool)} sx={{ mr: 1 }}>{tool.name}</Button>
          <Typography variant="caption">{tool.description}</Typography>
          </Box>
        ))}
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <Button variant="text" onClick={() => setShowCustom(!showCustom)}>{showCustom ? "Cancel" : "Add a custom tool"}</Button>
      </Box>
      {showCustom && (
        <Box sx={{ mb: 2, pl: 2 }}>
          {/* Tool Name and Invocation Type on one line */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <TextField
              label="Tool Name"
              value={customTool.name}
              onChange={e => setCustomTool(t => ({ ...t, name: e.target.value }))}
              sx={{ mr: 1 }}
            />
            <Autocomplete
              options={["python_function", "http_request", "javascript_function"]}
              value={customTool.invocation?.type || "python_function"}
              onChange={(_, value) => setCustomTool(t => ({ ...t, invocation: { type: value } }))}
              renderInput={params => <TextField {...params} label="Invocation Type" />}
              sx={{ width: 180, mr: 1 }}
              disableClearable
            />
          </Box>
          {/* Description on a separate line */}
          <TextField
            label="Description"
            value={customTool.description}
            onChange={e => setCustomTool(t => ({ ...t, description: e.target.value }))}
            sx={{ mb: 1, width: "100%" }}
          />
        {/* Parameters UI: allow adding multiple parameters with name, type, description */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Parameters:</Typography>
          {customTool.params.map((param, idx) => (
          <Box key={idx} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <TextField
            label="Name"
            value={param.name || ""}
            onChange={e => {
              const params = [...customTool.params];
              params[idx].name = e.target.value;
              setCustomTool(t => ({ ...t, params }));
            }}
            sx={{ mr: 1 }}
            />
            <Autocomplete
            options={["string", "number", "boolean", "json", "array"]}
            value={param.type || "string"}
            onChange={(_, value) => {
              const params = [...customTool.params];
              params[idx].type = value;
              setCustomTool(t => ({ ...t, params }));
            }}
            renderInput={params => <TextField {...params} label="Type" />}
            sx={{ width: 120, mr: 1 }}
            />
            <TextField
            label="Description"
            value={param.description || ""}
            onChange={e => {
              const params = [...customTool.params];
              params[idx].description = e.target.value;
              setCustomTool(t => ({ ...t, params }));
            }}
            sx={{ mr: 1, width: 180 }}
            />
            <Button size="small" color="error" onClick={() => {
            const params = [...customTool.params];
            params.splice(idx, 1);
            setCustomTool(t => ({ ...t, params }));
            }}>Remove</Button>
          </Box>
          ))}
          <Button size="small" variant="outlined" onClick={() => setCustomTool(t => ({ ...t, params: [...t.params, { name: "", type: "string", description: "" }] }))}>Add Parameter</Button>
        </Box>
        <Button variant="contained" size="small" onClick={handleAddCustomTool}>Add</Button>
        </Box>
      )}
      {selectedTools.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Selected tools:</Typography>
          {selectedTools.map((tool, idx) => (
            <Box
              key={tool.name || idx}
              sx={{
                mb: 2,
                pl: 2,
                borderLeft: "2px solid #eee"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-end", mb: 1, gap: 2 }}>
                <Autocomplete
                  freeSolo
                  options={[]}
                  value={tool.name}
                  onInputChange={(_, value) => handleEditTool(idx, "name", value)}
                  renderInput={params => <TextField {...params} label="Tool Name" size="small" />}
                  sx={{ width: 180 }}
                />
                <Autocomplete
                  freeSolo
                  options={[]}
                  value={tool.description}
                  onInputChange={(_, value) => handleEditTool(idx, "description", value)}
                  renderInput={params => <TextField {...params} label="Description" size="small" />}
                  sx={{ width: 220 }}
                />
                {/* Invocation Type dropdown for each selected tool */}
                <Box sx={{ width: 180, mt: 2 }}>
                  <Autocomplete
                    options={["python_function", "http_request", "javascript_function"]}
                    value={tool.invocation?.type || "python_function"}
                    onChange={(_, value) => handleInvocationTypeChange(idx, value)}
                    renderInput={params => <TextField {...params} label="Invocation Type" size="small" />}
                    disableClearable
                  />
                </Box>
                <Button variant="text" size="small" color="error" onClick={() => handleToolRemove(tool.name)}>Remove</Button>
              </Box>
              {tool.params && tool.params.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Parameters:</Typography>
                  {tool.params.map((param, paramIdx) => (
                    <Box key={paramIdx} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <TextField
                        label="Name"
                        value={param.name}
                        onChange={e => handleEditParam(idx, paramIdx, "name", e.target.value)}
                        size="small"
                        sx={{ mr: 1, width: 120 }}
                      />
                      <Autocomplete
                        options={["string", "number", "boolean", "json"]}
                        value={param.type}
                        onChange={(_, value) => handleEditParam(idx, paramIdx, "type", value)}
                        renderInput={params => <TextField {...params} label="Type" size="small" />}
                        sx={{ width: 120, mr: 1 }}
                      />
                      <TextField
                        label="Description"
                        value={param.description}
                        onChange={e => handleEditParam(idx, paramIdx, "description", e.target.value)}
                        size="small"
                        sx={{ mr: 1, width: 180 }}
                      />
                      <Button size="small" color="error" onClick={() => handleRemoveParam(idx, paramIdx)}>
                        Remove
                      </Button>
                    </Box>
                  ))}
                  <Button size="small" variant="outlined" onClick={() => handleAddParam(idx)}>
                    Add Parameter
                  </Button>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button onClick={() => setStep(1)}>Back</Button>
        <Button variant="contained" color="primary" onClick={() => setStep(3)}>
        Next
        </Button>
      </Box>
      </Box>
    );
  }

  // Step 3: LLM settings
  if (step === 3) {
    // Sync manual JSON and form values
    const handleManualSwitch = (checked) => {
      setManualMode(checked);
      if (checked) {
        // Populate manual JSON from form
        setManualJsonValue(JSON.stringify({
          llm: form.llm,
          temperature: form.temperature,
          max_tokens: form.max_tokens
        }, null, 2));
      } else {
        // Populate form from manual JSON
        try {
          const json = JSON.parse(manualJsonValue);
          setForm(f => ({
            ...f,
            llm: json.llm || "openai",
            temperature: typeof json.temperature === "number" ? json.temperature : 0,
            max_tokens: typeof json.max_tokens === "number" ? json.max_tokens : 4096
          }));
        } catch {
          // Ignore parse errors
        }
      }
    };

    return (
      <Box component={Paper} elevation={3} sx={{ maxWidth: 600, mx: "auto", my: 8, p: 4 }}>
        <Typography variant="h4" gutterBottom>Agent Creator</Typography>
        <Typography variant="h5" gutterBottom>LLM Settings</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Role: <b>{form.role}</b></Typography>
          <Typography variant="body2" color="text.secondary">Intent: <b>{form.name}</b></Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={manualMode} onChange={e => handleManualSwitch(e.target.checked)} />}
            label="Manual JSON mode"
          />
        </Box>
        {!manualMode ? (
          <Box>
            <Autocomplete
              options={llmOptions}
              value={form.llm}
              onChange={(_, value) => setForm(f => ({ ...f, llm: value || "openai" }))}
              renderInput={params => <TextField {...params} label="LLM Provider" />}
              sx={{ minWidth: 220, mb: 2 }}
              disableClearable
            />
            <TextField
              label="Temperature"
              type="number"
              value={form.temperature}
              onChange={e => setForm(f => ({ ...f, temperature: parseFloat(e.target.value) }))}
              sx={{ mb: 2 }}
              fullWidth
            />
            <TextField
              label="Max Tokens"
              type="number"
              value={form.max_tokens}
              onChange={e => setForm(f => ({ ...f, max_tokens: parseInt(e.target.value) }))}
              sx={{ mb: 2 }}
              fullWidth
            />
          </Box>
        ) : (
          <TextField
            label="LLM Settings JSON"
            multiline
            minRows={8}
            value={manualJsonValue}
            onChange={e => setManualJsonValue(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
        )}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button onClick={() => setStep(2)}>Back</Button>
          <Button variant="contained" color="primary" onClick={() => setStep(4)}>
            Next
          </Button>
        </Box>
      </Box>
    );
  }

  // Step 4: Agent creation (final step, if needed)
  // Always use selectedTools for review, not form.tools
  const reviewTools = selectedTools;
  // Convert tools to schema format (parameters array, not paramValues)
  const toolsForCard = reviewTools.map(tool => ({
    name: tool.name,
    description: tool.description,
    parameters: (tool.params || []).map(param => ({
      name: param.name,
      type: param.type,
      description: param.description || ""
    })),
    invocation: tool.invocation && tool.invocation.type
      ? { type: tool.invocation.type }
      : { type: "python_function" }
  }));
  // Compose llm_settings
  const llm_settings = {
    temperature: form.temperature,
    max_tokens: form.max_tokens
  };
  // Compose agent card
  const agentCard = {
    name: form.name,
    description: form.description,
    llm: form.llm,
    llm_settings,
    role: form.role,
    tools: toolsForCard,
    // Optionally add inputs/output if you have those steps
    owner: user?.username || "Unknown"
  };
  // When agent is created, update status to "created"
  const handleCreateAgent = async () => {
    setLoading(true);
    setError("");
    setSuccessMsg("");
    setSuccess("");
    try {
      let result;
      if (isEdit && editingAgentCard) {
        // Update agent: explicit null means the user removed the collection.
        const updatePayload = { ...agentCard };
        updatePayload.document_index_id = selectedDocIndex || null;
        if (selectedCodeIndex) updatePayload.code_index_id = selectedCodeIndex;
        result = await updateAgent(editingAgentCard.agent_id, updatePayload);
      } else {
        // Create agent
        result = await createAgent(agentCard, selectedDocIndex, selectedCodeIndex);
      }
      if (result.status === "success" || result.status === "started") {
        const kbDetailUrl = `/kb/${result.type === 'doc' ? 'doc' : 'code'}/${result.task_id}`;
        setSuccessMsg(
          `Agent ${isEdit ? "updated" : "created"} successfully! <a href='/agent/${result.agent_id}' target='_blank'>View agent detail</a><br/>` +
          (result.task_id ? `<a href='${kbDetailUrl}' target='_blank'>View collection status</a>` : "")
        );
        setSuccess("Agent saved.");
        if (onAgentCreated) onAgentCreated(result.agent_id);
      } else {
        setError(result.message || "Failed to save agent.");
      }
    } catch (e) {
      setError(e.message || "Failed to save agent.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component={Paper} elevation={3} sx={{ maxWidth: 600, mx: "auto", my: 8, p: 4 }}>
      <Typography variant="h4" gutterBottom>Agent Creator</Typography>
      <Typography variant="h5" gutterBottom>{isEdit ? "Review & Update Agent" : "Review & Create Agent"}</Typography>
      <Box sx={{ mb: 2 }}>
        {isEdit && (
          <Typography variant="body2" color="text.secondary">Agent ID: <b>{editingAgentCard.agent_id}</b></Typography>
        )}
        <Typography variant="body2" color="text.secondary">Role: <b>{form.role}</b></Typography>
        <Typography variant="body2" color="text.secondary">Intent: <b>{form.name}</b></Typography>
      </Box>
      {/* Agent Description */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>Agent Description</Typography>
        <Typography variant="body2">{form.description}</Typography>
      </Box>
      {/* Tools Review */}
      {reviewTools.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>Selected Tools</Typography>
          {reviewTools.map(tool => (
            <Box key={tool.name} sx={{ mb: 1, pl: 2, borderLeft: "2px solid #eee" }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>{tool.name} - {tool.description}</Typography>
              {tool.parameters && tool.parameters.length > 0 && (
                <Box sx={{ pl: 2 }}>
                  {tool.parameters.map(param => (
                    <Typography key={param.name} variant="caption" color="text.secondary">
                      {param.name} ({param.type}): {param.description}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      {/* LLM Settings Review */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>LLM Settings</Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>Provider: <b>{form.llm}</b></Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>Temperature: <b>{form.temperature}</b></Typography>
        <Typography variant="body2">Max Tokens: <b>{form.max_tokens}</b></Typography>
      </Box>
      {/* Associate Indexes */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>Associate Indexes (optional)</Typography>
        <Autocomplete
          options={docIndexes}
          getOptionLabel={opt => opt.name || opt.index_id}
          value={docIndexes.find(i => i.index_id === selectedDocIndex) || null}
          onChange={(_, value) => setSelectedDocIndex(value ? value.index_id : null)}
          renderInput={params => <TextField {...params} label="Document collection" />}
          sx={{ mb: 2 }}
        />
        <Autocomplete
          options={codeIndexes}
          getOptionLabel={opt => opt.name || opt.index_id}
          value={codeIndexes.find(i => i.index_id === selectedCodeIndex) || null}
          onChange={(_, value) => setSelectedCodeIndex(value ? value.index_id : null)}
          renderInput={params => <TextField {...params} label="Code collection" />}
        />
      </Box>
      {/* Confirmation & Create/Update Button */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateAgent}
          disabled={loading}
          sx={{ width: "100%", maxWidth: 300 }}
        >
          {loading ? <CircularProgress size={24} /> : isEdit ? "Update" : "Create"}
        </Button>
        <Button
          onClick={() => setStep(3)}
          sx={{ mt: 2, color: "text.secondary" }}
        >
          Go back to edit
        </Button>
      </Box>
      {successMsg && (
        <Box sx={{ mt: 2 }}>
          <Typography color="success.main" sx={{ mb: 1 }}>
            <span dangerouslySetInnerHTML={{ __html: successMsg }} />
          </Typography>
        </Box>
      )}
      {success && (
        <Box sx={{ mt: 3 }}>
          <Typography color="success.main" sx={{ mb: 1 }}>
            <span dangerouslySetInnerHTML={{ __html: success }} />
          </Typography>
        </Box>
      )}
      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
    </Box>
  );
}
